import React from "react";
import { Linking, View } from "react-native";
import { FreeplayEntry, FreeplayEvent, FreeplayLeague } from "@freeplay/shared-code";
import { EStyleSheet, useDynamicStyles } from "react-native-extended-stylesheet-typescript";
import { BetDisplay } from "../components/BetDisplay";
import { ButtonSize, ButtonTheme, RoundedButton } from "../components/buttons/roundedButton/RoundedButton";
import { MediaQueries } from "../components/hooks/dynamicStyleHook";
import { useServices } from "../contexts/ServiceContext";
import { GlobalStyles } from "../components/style/Style";
import { GameTitle } from "../components/picks/GameTitle";
import format from "date-fns/format";
import parseISO from "date-fns/parseISO";
import DefaultImage from "../assets/default_background.png";
import DefaultTSImage from "../assets/default_background_ts.png";
import { ThanksForPlaying } from "../components/submitted/ThanksForPlaying";
import { useApplication } from "../contexts/ApplicationContext";

interface PicksSubmittedProps {
    event: FreeplayEvent;
    entry: FreeplayEntry;
    handlePicks: () => void;
    handleLeaderboard: () => void;
}

export const PicksSubmittedView: React.FC<PicksSubmittedProps> = ({ event, entry, handlePicks, handleLeaderboard }) => {
    const { edgeDeploy } = useServices();
    const { freeplayFocusedExperience } = useApplication().appState;
    const styles = useDynamicStyles(generateStyles);

    return (
        <>
            <GameTitle
                title={event.name}
                date={format(parseISO(event.bettingEnds), "MMMM dd, yyyy")}
                backgroundImage={event.game.league === FreeplayLeague.Other ? DefaultTSImage : DefaultImage}
            />
            <View style={edgeDeploy ? styles.edgeContainer : styles.container}>
                <ThanksForPlaying />

                {event.platformEvents.length > 0 && (
                    <BetDisplay
                        platformEvents={event.platformEvents}
                        entry={entry.betOutcomes}
                        buttonText={event.buttonText}
                        buttonURL={event.buttonUrl}
                    />
                )}

                <View style={[styles.buttonPadding, edgeDeploy && styles.edgeButtonPadding]}>
                    <RoundedButton
                        onPress={handlePicks}
                        testID={"view-picks-button"}
                        config={{
                            buttonText: "VIEW MY PICKS",
                            width: 250,
                            height: 50,
                            size: ButtonSize.LARGE,
                            theme: ButtonTheme.ACCENTED,
                            customStyle: edgeDeploy ? styles.edgeButton : {},
                        }}
                    />
                    {freeplayFocusedExperience ? null : (
                        <RoundedButton
                            onPress={handleLeaderboard}
                            testID={"leaderboard-button"}
                            config={{
                                buttonText: "LEADERBOARD",
                                size: ButtonSize.LARGE,
                                width: 250,
                                height: 50,
                                theme: ButtonTheme.ACCENTED,
                                customStyle: edgeDeploy ? styles.edgeButton : {},
                                margin: { bottom: 0 },
                            }}
                        />
                    )}
                </View>
            </View>
            {event.platformEvents.length <= 0 && event.buttonText && event.buttonUrl && event.buttonUrl !== "" && (
                <View style={styles.ctaContainer}>
                    <RoundedButton
                        config={{
                            buttonText: event.buttonText,
                            size: ButtonSize.LARGE,
                            width: 250,
                            height: 50,
                            customStyle: edgeDeploy ? styles.edgeButton : {},
                            theme: ButtonTheme.CTA,
                            margin: { bottom: 0 },
                        }}
                        testID={`next-game-button`}
                        onPress={() => Linking.openURL(event.buttonUrl)}
                    />
                </View>
            )}
        </>
    );
};

const generateStyles = () => {
    return EStyleSheet.create({
        [MediaQueries.MEDIUM]: {
            edgeContainer: {
                padding: 15,
                borderRadius: 0,
                marginVertical: 0,
            },
        },
        [MediaQueries.SMALL]: {
            edgeBottomButtonsContainer: {
                height: 90,
            },
            edgeButtonPadding: {
                height: 140,
            },
            edgeButton: {
                width: 250,
                height: 50,
            },
        },
        edgeContainer: {
            width: "100%",
            maxWidth: 860,
            marginHorizontal: "auto",
            padding: 30,
            marginVertical: 30,
            backgroundColor: () => EStyleSheet.value(GlobalStyles.BACKGROUND_COLOUR),
            borderRadius: () => EStyleSheet.value(GlobalStyles.BORDER_RADIUS),
            flex: 1,
            flexGrow: 1,
            flexDirection: "column",
            alignSelf: "stretch",
            justifyContent: "center",
            overflowY: "auto",
        },
        container: {
            flex: 1,
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            alignSelf: "stretch",
            height: "100%",
            overflowY: "auto",
        },
        buttonPadding: {
            alignItems: "center",
            justifyContent: "center",
            width: 250,
            height: 110,
            marginTop: 20,
            marginBottom: 20,
        },
        edgeButtonPadding: {
            height: 160,
            marginBottom: 0,
        },
        edgeButton: {
            width: 300,
            height: 60,
        },
        ctaContainer: {
            marginTop: "auto",
            width: "100%",
            height: 70,
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: () => EStyleSheet.value(GlobalStyles.SUBMITTED_CTA_BACKGROUND),
        },
    });
};
